import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  Appointment,
  AppointmentStatus,
  UpdateAppointmentInput,
} from "../lib/api";

function getStatusBadgeClasses(status: AppointmentStatus): string {
  const baseClasses =
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  switch (status) {
    case "scheduled":
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case "confirmed":
      return `${baseClasses} bg-green-100 text-green-800`;
    case "in_progress":
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case "completed":
      return `${baseClasses} bg-gray-100 text-gray-800`;
    case "cancelled":
      return `${baseClasses} bg-red-100 text-red-800`;
    case "no_show":
      return `${baseClasses} bg-purple-100 text-purple-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800`;
  }
}

function formatStatusLabel(status: AppointmentStatus): string {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime24(dateString: string): string {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const mins = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${mins}`;
}

function getDurationMinutes(startTime: string, endTime: string): number {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return Math.round((end.getTime() - start.getTime()) / 60000);
}

function formatPrice(price: string | null): string {
  if (!price) return "-";
  const num = parseFloat(price);
  return `$${num.toFixed(2)}`;
}

function formatAddress(address: { street?: string; city?: string; state?: string; zipCode?: string } | null | undefined): string | null {
  if (!address) return null;
  const parts = [address.street, address.city, address.state, address.zipCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

interface CancelModalProps {
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}

function CancelModal({ onCancel, onConfirm, isPending }: CancelModalProps) {
  const [reason, setReason] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(reason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Cancel Appointment</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="cancelReason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Cancellation Reason
            </label>
            <textarea
              id="cancelReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              placeholder="Optional reason for cancellation..."
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? "Cancelling..." : "Cancel Appointment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Details Modal - ONLY notes and price
interface EditDetailsModalProps {
  appointment: Appointment;
  onClose: () => void;
  onSuccess: () => void;
}

function EditDetailsModal({ appointment, onClose, onSuccess }: EditDetailsModalProps) {
  const [notes, setNotes] = useState(appointment.notes ?? "");
  const [price, setPrice] = useState(appointment.price ?? "");
  const [error, setError] = useState("");

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAppointmentInput) =>
      api.appointments.update(appointment.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update appointment");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    updateMutation.mutate({
      notes: notes.trim() || null,
      price: price.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Details</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="editPrice"
              className="block text-sm font-medium text-gray-700"
            >
              Price
            </label>
            <input
              id="editPrice"
              type="text"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div>
            <label
              htmlFor="editNotes"
              className="block text-sm font-medium text-gray-700"
            >
              Notes
            </label>
            <textarea
              id="editNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reschedule Modal - ONLY date/time
interface RescheduleModalProps {
  appointment: Appointment;
  onClose: () => void;
  onSuccess: () => void;
}

function RescheduleModal({ appointment, onClose, onSuccess }: RescheduleModalProps) {
  const [startDate, setStartDate] = useState(
    new Date(appointment.startTime).toISOString().split("T")[0]
  );
  const [startTimeValue, setStartTimeValue] = useState(
    new Date(appointment.startTime).toTimeString().slice(0, 5)
  );
  const [endTimeValue, setEndTimeValue] = useState(
    new Date(appointment.endTime).toTimeString().slice(0, 5)
  );
  const [error, setError] = useState("");

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAppointmentInput) =>
      api.appointments.update(appointment.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to reschedule appointment");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!startDate || !startTimeValue || !endTimeValue) {
      setError("Date and time are required");
      return;
    }

    const startTime = new Date(`${startDate}T${startTimeValue}`).toISOString();
    const endTime = new Date(`${startDate}T${endTimeValue}`).toISOString();

    updateMutation.mutate({
      startTime,
      endTime,
    });
  };

  // Calculate duration for display
  const duration = getDurationMinutes(appointment.startTime, appointment.endTime);
  const durationDisplay = duration >= 60
    ? `${Math.floor(duration / 60)}h ${duration % 60}m`
    : `${duration}m`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-2">Reschedule Appointment</h2>
        <p className="text-sm text-gray-600 mb-4">
          Current: {formatDate(appointment.startTime)} at {formatTime24(appointment.startTime)}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="rescheduleDate"
              className="block text-sm font-medium text-gray-700"
            >
              New Date
            </label>
            <input
              id="rescheduleDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="rescheduleStartTime"
                className="block text-sm font-medium text-gray-700"
              >
                Start Time
              </label>
              <input
                id="rescheduleStartTime"
                type="time"
                value={startTimeValue}
                onChange={(e) => setStartTimeValue(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="rescheduleEndTime"
                className="block text-sm font-medium text-gray-700"
              >
                End Time
              </label>
              <input
                id="rescheduleEndTime"
                type="time"
                value={endTimeValue}
                onChange={(e) => setEndTimeValue(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>

          <div className="text-sm text-gray-500">
            Duration: {durationDisplay}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? "Rescheduling..." : "Reschedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteModalProps {
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

function DeleteModal({ onCancel, onConfirm, isPending }: DeleteModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Appointment?</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this appointment? This action cannot
          be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface StatusActionsProps {
  status: AppointmentStatus;
  onStatusChange: (newStatus: AppointmentStatus) => void;
  isPending: boolean;
}

function StatusActions({
  status,
  onStatusChange,
  isPending,
}: StatusActionsProps) {
  const buttonBase =
    "px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed";

  const renderButtons = () => {
    switch (status) {
      case "scheduled":
        return (
          <button
            onClick={() => onStatusChange("confirmed")}
            disabled={isPending}
            className={`${buttonBase} text-white bg-green-600 hover:bg-green-700`}
          >
            Confirm
          </button>
        );
      case "confirmed":
        return (
          <button
            onClick={() => onStatusChange("in_progress")}
            disabled={isPending}
            className={`${buttonBase} text-white bg-yellow-600 hover:bg-yellow-700`}
          >
            Start Session
          </button>
        );
      case "in_progress":
        return (
          <button
            onClick={() => onStatusChange("completed")}
            disabled={isPending}
            className={`${buttonBase} text-white bg-gray-600 hover:bg-gray-700`}
          >
            Complete
          </button>
        );
      case "completed":
      case "cancelled":
      case "no_show":
        return null;
      default:
        return null;
    }
  };

  const buttons = renderButtons();
  if (!buttons) return null;

  return <div className="mb-5">{buttons}</div>;
}

// Location display component
function LocationDisplay({ appointment }: { appointment: Appointment }) {
  const locationType = appointment.locationType || "facility";
  
  switch (locationType) {
    case "facility":
      return (
        <div className="text-gray-900">
          📍 At {appointment.calendar?.name || "facility"}
        </div>
      );
    case "client_home": {
      const address = formatAddress(appointment.client?.address);
      const googleMapsUrl = address 
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
        : null;
      return (
        <div className="text-gray-900">
          🏠 At client's home
          {address && (
            <div className="text-sm text-gray-600 mt-1">
              {googleMapsUrl ? (
                <a 
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-600 hover:text-amber-700"
                >
                  {address} →
                </a>
              ) : (
                address
              )}
            </div>
          )}
        </div>
      );
    }
    case "saved_location": {
      const savedLoc = appointment.savedLocation;
      return (
        <div className="text-gray-900">
          📍 {savedLoc?.name || "Saved location"}
          {savedLoc?.googleMapsUrl && (
            <div className="text-sm mt-1">
              <a 
                href={savedLoc.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 hover:text-amber-700"
              >
                View on map →
              </a>
            </div>
          )}
        </div>
      );
    }
    case "virtual":
      return (
        <div className="text-gray-900">
          💻 Virtual session
        </div>
      );
    default:
      return <div className="text-gray-900">📍 Location not specified</div>;
  }
}

// Behavioral alert banner
function BehavioralAlertBanner({ notes }: { notes: string }) {
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <span className="text-yellow-400 text-lg">⚠️</span>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Behavioral Alert</h3>
          <p className="mt-1 text-sm text-yellow-700">{notes}</p>
        </div>
      </div>
    </div>
  );
}

// Commands chips display
function CommandsChips({ commands }: { commands: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {commands.map((cmd) => (
        <span
          key={cmd}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
        >
          ✓ {cmd}
        </span>
      ))}
    </div>
  );
}

// Visit number badge for program services
function VisitNumberBadge({ enrollment }: { enrollment: { appointmentsCompleted: number } }) {
  const visitNumber = enrollment.appointmentsCompleted + 1;
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
      APPT #{visitNumber}
    </span>
  );
}

// Sales Demo Notes banner for Free Demonstration appointments
function SalesDemoNotesBanner({ notes }: { notes: string }) {
  return (
    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">📋</span>
        <span className="font-bold text-green-800 text-sm">Sales Demo Notes</span>
      </div>
      <p className="text-sm text-green-700 leading-relaxed">{notes}</p>
    </div>
  );
}

// Stylized monospace time display
function TimeRangeDisplay({ startTime, endTime }: { startTime: string; endTime: string }) {
  return (
    <div className="font-mono text-lg font-semibold text-gray-900">
      {formatTime24(startTime)} — {formatTime24(endTime)}
    </div>
  );
}

// Pet Details card
function PetDetailsCard({
  name,
  breed,
  duration
}: {
  name: string;
  breed: string | null;
  duration: number;
}) {
  const durationDisplay = duration >= 60
    ? `${Math.floor(duration / 60)} hour${Math.floor(duration / 60) !== 1 ? 's' : ''}`
    : `${duration} min`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="text-base font-semibold text-gray-900 mb-3">{name}</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Breed</div>
          <div className="text-sm font-medium text-gray-900">{breed || "Unknown"}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Duration</div>
          <div className="text-sm font-medium text-gray-900">{durationDisplay}</div>
        </div>
      </div>
    </div>
  );
}

// Owner Information card
interface OwnerInfoProps {
  name: string;
  phone: string | null;
  email: string | null;
  clientId: string;
}

function OwnerInformationCard({ name, phone, email, clientId }: OwnerInfoProps) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="text-base font-semibold text-gray-900 mb-2">{name}</div>
      {phone && (
        <div className="font-mono text-sm text-gray-500 mb-1">{phone}</div>
      )}
      {email && (
        <div className="text-sm text-gray-500 mb-4">{email}</div>
      )}
      <button
        onClick={() => navigate(`/clients/${clientId}`)}
        className="w-full px-4 py-2.5 bg-gray-50 text-gray-900 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-100 flex items-center justify-center gap-2"
      >
        <span>📇</span> View Contact
      </button>
    </div>
  );
}

// More Actions dropdown
interface MoreActionsDropdownProps {
  onDelete: () => void;
}

function MoreActionsDropdown({ onDelete }: MoreActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
        aria-label="More actions"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
            <button
              onClick={() => {
                setIsOpen(false);
                onDelete();
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-md"
            >
              Delete Appointment
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppointmentDetailPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    data: appointment,
    isLoading: isLoadingAppointment,
    error: appointmentError,
  } = useQuery({
    queryKey: ["appointments", appointmentId],
    queryFn: () => api.appointments.get(appointmentId!),
    enabled: !!appointmentId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAppointmentInput) =>
      api.appointments.update(appointmentId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["appointments", appointmentId],
      });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.appointments.delete(appointmentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      navigate("/appointments");
    },
  });

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    updateMutation.mutate({ status: newStatus });
  };

  const handleCancel = (reason: string) => {
    updateMutation.mutate(
      {
        status: "cancelled",
        cancellationReason: reason || null,
      },
      {
        onSuccess: () => {
          setShowCancelModal(false);
        },
      }
    );
  };

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["appointments", appointmentId],
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (!appointmentId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Invalid appointment ID</p>
        </div>
      </div>
    );
  }

  if (isLoadingAppointment) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse text-gray-500">Loading appointment...</div>
      </div>
    );
  }

  if (appointmentError) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {appointmentError instanceof ApiError
              ? appointmentError.message
              : "Failed to load appointment"}
          </p>
          <Link
            to="/appointments"
            className="mt-2 inline-block text-amber-600 hover:text-amber-700"
          >
            Back to appointments
          </Link>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-8">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700">Appointment not found</p>
          <Link
            to="/appointments"
            className="mt-2 inline-block text-amber-600 hover:text-amber-700"
          >
            Back to appointments
          </Link>
        </div>
      </div>
    );
  }

  const duration = getDurationMinutes(appointment.startTime, appointment.endTime);

  // Use rich nested data if available, fall back to separate queries
  const clientName = appointment.client
    ? `${appointment.client.firstName} ${appointment.client.lastName}`
    : appointment.clientFirstName && appointment.clientLastName
      ? `${appointment.clientFirstName} ${appointment.clientLastName}`
      : "Loading...";

  const dogName = appointment.dog?.name ?? null;
  const dogBreed = appointment.dog?.breed ?? null;
  const dogBehavioralNotes = appointment.dog?.behavioralNotes ?? null;
  const dogCommands = appointment.dog?.commandsLearned ?? null;

  const calendarName = appointment.calendar?.name ?? "Loading...";
  const serviceName = appointment.service?.name ?? appointment.serviceName ?? "Loading...";
  const isProgram = appointment.service?.isProgram ?? false;

  // Check if this is a Free Demonstration with sales notes
  const isFreeDemo = serviceName === "Free Demonstration";
  // Sales notes would come from the notes field for Free Demonstration appointments
  const salesNotes = isFreeDemo ? appointment.notes : null;
  
  const isCancelled = appointment.status === "cancelled";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/appointments"
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ←
            </Link>
            <div>
              <div className="font-mono text-xs text-gray-500">
                {appointment.id}
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {dogName ?? clientName}
              </h1>
            </div>
          </div>
          <MoreActionsDropdown onDelete={() => setShowDeleteModal(true)} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {/* Sales Demo Notes Banner - for Free Demonstration appointments */}
        {isFreeDemo && salesNotes && (
          <SalesDemoNotesBanner notes={salesNotes} />
        )}

        {/* Behavioral alert banner */}
        {dogBehavioralNotes && <BehavioralAlertBanner notes={dogBehavioralNotes} />}

        {/* Service & Visit Number */}
        <div className="flex gap-3 mb-5 flex-wrap">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
              Service
            </label>
            <span className={getStatusBadgeClasses(appointment.status)}>
              {serviceName}
            </span>
          </div>
          {isProgram && appointment.enrollment && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Visit
              </label>
              <VisitNumberBadge enrollment={appointment.enrollment} />
            </div>
          )}
        </div>

        {/* Location */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
            Location
          </label>
          <div className="bg-gray-100 rounded-md px-3 py-3 border border-gray-200">
            <LocationDisplay appointment={appointment} />
          </div>
        </div>

        {/* Pet Details Card */}
        {dogName && (
          <div className="mb-5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
              Pet Details
            </label>
            <PetDetailsCard
              name={dogName}
              breed={dogBreed}
              duration={duration}
            />
          </div>
        )}

        {/* Appointment Time */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
            Appointment Time
          </label>
          <TimeRangeDisplay
            startTime={appointment.startTime}
            endTime={appointment.endTime}
          />
          <div className="text-sm text-gray-500 mt-1">
            {formatDate(appointment.startTime)}
          </div>
        </div>

        {/* Commands Learned */}
        {dogCommands && dogCommands.length > 0 && (
          <div className="mb-5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
              Commands
            </label>
            <CommandsChips commands={dogCommands} />
          </div>
        )}

        {/* Owner Information Card */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
            Owner Information
          </label>
          <OwnerInformationCard
            name={clientName}
            phone={appointment.client?.phone ?? null}
            email={appointment.client?.email ?? null}
            clientId={appointment.clientId}
          />
        </div>

        {/* Notes (if not Free Demo - those are shown above) */}
        {appointment.notes && !isFreeDemo && (
          <div className="mb-5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
              Notes
            </label>
            <div className="bg-amber-50 px-3 py-3 rounded-md border-l-3 border-amber-400">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.notes}</p>
            </div>
          </div>
        )}

        {/* Cancellation Reason */}
        {appointment.cancellationReason && (
          <div className="mb-5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
              Cancellation Reason
            </label>
            <p className="text-red-700 text-sm whitespace-pre-wrap">
              {appointment.cancellationReason}
            </p>
          </div>
        )}

        {/* Price and Status Info */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Price
            </label>
            <div className="text-gray-900 font-medium">{formatPrice(appointment.price)}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Status
            </label>
            <span className={getStatusBadgeClasses(appointment.status)}>
              {formatStatusLabel(appointment.status)}
            </span>
          </div>
        </div>

        {/* Calendar */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
            Calendar
          </label>
          <div className="text-gray-900">{calendarName}</div>
        </div>

        {/* Status Actions (workflow buttons) */}
        <StatusActions
          status={appointment.status}
          onStatusChange={handleStatusChange}
          isPending={updateMutation.isPending}
        />

        {/* Main Actions Row: Edit Details | Reschedule | Cancel */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setShowEditDetailsModal(true)}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Edit Details
          </button>
          <button
            onClick={() => setShowRescheduleModal(true)}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
          >
            Reschedule
          </button>
          {!isCancelled && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {showCancelModal && (
        <CancelModal
          onCancel={() => setShowCancelModal(false)}
          onConfirm={handleCancel}
          isPending={updateMutation.isPending}
        />
      )}

      {showEditDetailsModal && appointment && (
        <EditDetailsModal
          appointment={appointment}
          onClose={() => setShowEditDetailsModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {showRescheduleModal && appointment && (
        <RescheduleModal
          appointment={appointment}
          onClose={() => setShowRescheduleModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
